import React from "react";

import { AvaLoadingAnimation } from "@doitintl/ava-components/src/Messenger/AvaLoadingAnimation";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Alert, AlertTitle, Paper, Stack, Typography } from "@mui/material";
import { common } from "@mui/material/colors";
import { type SxProps, useTheme } from "@mui/material/styles";

import { cmpBaseColors, mainColors } from "../../../cmpBaseColors";
import { createGradientWithOpacity, createTextGradient } from "../../../utils/color";
import { MessengerFooter } from "../MessengerFooter";

type AvaQuickPromptProps = {
  title: string;
  description: string;
  isLoading?: boolean;
  disabled?: boolean;
  showFileAttachment?: boolean;
  showStopButton?: boolean;
  onAskQuestionClicked: (question: string, files?: File[]) => Promise<void>;
  handleStopGeneration: () => void;
  error?: {
    title?: string;
    description: string;
  };
  sx?: SxProps;
  maxWidth?: string;
};

export const AvaQuickPrompt: React.FC<AvaQuickPromptProps> = ({
  title,
  description,
  isLoading = false,
  disabled = false,
  showFileAttachment = false,
  showStopButton = false,
  onAskQuestionClicked,
  handleStopGeneration,
  maxWidth = "500px",
  error,
  ...stackProps
}) => {
  const theme = useTheme();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      textAlign="center"
      ml="auto"
      mr="auto"
      maxWidth={maxWidth}
      data-cy="ava-quick-prompt-container"
      {...stackProps}
    >
      <Paper
        sx={{
          position: "relative",
          backgroundColor:
            theme.palette.mode === "light" ? cmpBaseColors.backgroundLight : cmpBaseColors.backgroundDark,
          background:
            theme.palette.mode === "light"
              ? `${createGradientWithOpacity(cmpBaseColors.gradientLightStart, cmpBaseColors.gradientLightEnd, 0.04)}, ${cmpBaseColors.backgroundLight}`
              : `${createGradientWithOpacity(cmpBaseColors.gradientDarkStart, cmpBaseColors.gradientDarkBackgroundEnd, 0.04)}, ${cmpBaseColors.backgroundDark}`,
          width: "100%",
          px: 2,
          pt: 2,
          pb: isLoading ? 0 : 2,
          borderRadius: 4,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 4,
            padding: "1px",
            background:
              theme.palette.mode === "light"
                ? createGradientWithOpacity(cmpBaseColors.gradientLightStart, cmpBaseColors.gradientLightEnd, 0.2)
                : createGradientWithOpacity(cmpBaseColors.gradientDarkStart, cmpBaseColors.gradientDarkBorderEnd, 0.2),
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "xor",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            pointerEvents: "none",
          },
        }}
        elevation={0}
        data-cy="ava-quick-prompt-paper"
      >
        <Stack justifyContent="flex-start" mb={1}>
          <Stack direction="row" gap={1}>
            <AutoAwesomeIcon
              sx={{
                mt: 0.6,
                width: 16,
                height: 16,
                color: theme.palette.mode === "light" ? theme.palette.primary.dark : theme.palette.primary.main,
              }}
              data-cy="ava-quick-prompt-icon"
            />
            <Typography
              textAlign="left"
              variant="subtitle1"
              fontWeight={500}
              mb={1.5}
              sx={
                theme.palette.mode === "light"
                  ? createTextGradient(cmpBaseColors.gradientLightStart, cmpBaseColors.gradientLightEnd)
                  : createTextGradient(cmpBaseColors.gradientDarkTextStart, cmpBaseColors.gradientDarkTextEnd)
              }
              data-cy="ava-quick-prompt-title"
            >
              {title}
            </Typography>
          </Stack>

          <Typography textAlign="left" variant="body2" data-cy="ava-quick-prompt-description">
            {description}
          </Typography>
        </Stack>

        <MessengerFooter
          disableInput={disabled || isLoading}
          onAskQuestionClicked={onAskQuestionClicked}
          showStopButton={showStopButton}
          handleStopGeneration={handleStopGeneration}
          resetQuestion={false}
          showFileAttachment={showFileAttachment}
          sx={{
            backgroundColor: theme.palette.mode === "light" ? theme.palette.common.white : cmpBaseColors.darkContrast,
            border: "none",
            p: 0,
          }}
          textFieldProps={{
            placeholder: undefined,
            label: undefined,
          }}
          textFieldSx={{
            "& .MuiOutlinedInput-input": {
              py: 1.25,
              pb: 1.25,
            },
            "& .MuiOutlinedInput-root": {
              pb: 0,
              pt: 0,
              pr: 4,
              pl: showFileAttachment ? 4 : 1.8,
            },
          }}
          iconWrapperSx={{
            bottom: 2,
            right: 2,
          }}
          fileAttachmentSx={{
            bottom: 6,
            left: 3,
          }}
          data-cy="ava-quick-prompt-messenger-footer"
        />
        <Stack justifyContent="center" alignItems="center">
          {isLoading && (
            <AvaLoadingAnimation
              width={60}
              height={40}
              isDarkMode={theme.palette.mode === "dark"}
              primaryColor={theme.palette.mode === "light" ? mainColors.ink : common.white}
              secondaryColor={theme.palette.mode === "light" ? mainColors.spark : cmpBaseColors.accentDark}
              data-cy="ava-quick-prompt-loading"
            />
          )}
        </Stack>

        {error && (
          <Alert
            variant="standard"
            severity={"warning"}
            sx={{
              mt: 2,
              textAlign: "left",
              "& .MuiAlert-message": {
                pt: 0.5,
              },
            }}
          >
            {error.title && <AlertTitle sx={{ textAlign: "left" }}>{error.title}</AlertTitle>}
            <Typography variant="body2" sx={{ textAlign: "left" }}>
              {error.description}
            </Typography>
          </Alert>
        )}
      </Paper>
    </Stack>
  );
};
